import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/Layout/MainLayout';
import httpClient from '../../utils/httpClient';

interface SubscriptionPlan {
  priceId: string;
  priceValue: string;
  isProductionPlan?: boolean;
  plansSubscription: number;
  name: string;
  description: string;
  commercialName: string;
  currency: string;
  paymentFrequence: string;
}

const Subscription: React.FC = () => {
  const { t } = useTranslation();
  const [tabIndex, setTabIndex] = useState(0);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState<boolean>(false);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [submittingPriceId, setSubmittingPriceId] = useState<string | null>(null);

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const payments = [
    {
      id: '1',
      date: '05/02/2026',
      description: 'Assinatura Plano Standard - Fevereiro/2026',
      amount: 'R$ 49,90',
      status: 'paid'
    },
    {
      id: '2',
      date: '05/01/2026',
      description: 'Assinatura Plano Standard - Janeiro/2026',
      amount: 'R$ 49,90',
      status: 'paid'
    },
    {
      id: '3',
      date: '05/12/2025',
      description: 'Assinatura Plano Standard - Dezembro/2025',
      amount: 'R$ 49,90',
      status: 'failed'
    },
    {
      id: '4',
      date: '05/11/2025',
      description: 'Assinatura Plano Standard - Novembro/2025',
      amount: 'R$ 49,90',
      status: 'pending'
    }
  ] as const;

  const mapStatusLabel = (status: string) => {
    if (status === 'paid') return t('subscription_payments_status_paid');
    if (status === 'pending') return t('subscription_payments_status_pending');
    if (status === 'failed') return t('subscription_payments_status_failed');
    return status;
  };

  useEffect(() => {
    const loadPlans = async () => {
      try {
        setLoadingPlans(true);
        setPlansError(null);
        const res = await httpClient.get('/api/v1/portal/subscription/list-available-plans');
        const payload = res?.data;
        const ok = payload?.success === true && Number(payload?.status) === 200;
        const list: SubscriptionPlan[] = Array.isArray(payload?.data) ? payload.data : [];
        if (!ok) {
          setPlans([]);
          setPlansError(t('subscription_error_load_plans'));
          return;
        }
        setPlans(list);
      } catch (error: any) {
        setPlans([]);
        setPlansError(t('subscription_error_load_plans'));
      } finally {
        setLoadingPlans(false);
      }
    };

    loadPlans();
  }, [t]);

  const handleSubscribe = async (priceId: string) => {
    try {
      setSubmittingPriceId(priceId);
      setPlansError(null);
      await httpClient.post('/api/v1/portal/subscription/checkout', { priceId });
    } catch (error: any) {
      setPlansError(t('subscription_error_subscribe'));
    } finally {
      setSubmittingPriceId(null);
    }
  };

  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
            {t('subscription_title')}
          </Typography>

          <Box sx={{ display: 'flex', minHeight: 400 }}>
            <Tabs
              orientation="vertical"
              value={tabIndex}
              onChange={handleChangeTab}
              sx={{ borderRight: 1, borderColor: 'divider', minWidth: 220 }}
            >
              <Tab label={t('subscription_tab_plan')} />
              <Tab label={t('subscription_tab_payments')} />
            </Tabs>

            <Box sx={{ flexGrow: 1, pl: 4 }}>
              {tabIndex === 0 && (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {loadingPlans && (
                    <Typography variant="body2" color="text.secondary">
                      {t('subscription_loading')}
                    </Typography>
                  )}
                  {!loadingPlans && plansError && (
                    <Typography variant="body2" color="error">
                      {plansError}
                    </Typography>
                  )}
                  {!loadingPlans && !plansError && plans.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {t('subscription_no_plans')}
                    </Typography>
                  )}
                  {!loadingPlans && !plansError && plans.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
                      {plans.map((plan) => (
                        <Card key={plan.priceId} sx={{ maxWidth: 420, width: '100%', position: 'relative', overflow: 'visible' }}>
                          {plan.isProductionPlan === false && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 14,
                                right: 14,
                                px: 1.5,
                                py: 0.5,
                                bgcolor: '#ff9800',
                                color: '#fff',
                                borderRadius: 1,
                                fontSize: '0.65rem',
                                fontWeight: 'bold',
                              }}
                            >
                              Sandbox
                            </Box>
                          )}
                          <CardContent>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                              {plan.commercialName || t('subscription_plan_standard_title')}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, mt: 1 }}>
                              {plan.priceValue}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {plan.description || t('subscription_plan_standard_description')}
                            </Typography>
                          </CardContent>
                          <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => handleSubscribe(plan.priceId)}
                              disabled={Boolean(submittingPriceId)}
                            >
                              {submittingPriceId === plan.priceId ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                t('subscription_plan_standard_button')
                              )}
                            </Button>
                          </CardActions>
                        </Card>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {tabIndex === 1 && (
                <Box sx={{ mt: 1 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('subscription_payments_table_date')}</TableCell>
                        <TableCell>{t('subscription_payments_table_description')}</TableCell>
                        <TableCell>{t('subscription_payments_table_amount')}</TableCell>
                        <TableCell>{t('subscription_payments_table_status')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payments.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell>{payment.date}</TableCell>
                          <TableCell>{payment.description}</TableCell>
                          <TableCell>{payment.amount}</TableCell>
                          <TableCell>{mapStatusLabel(payment.status)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
    </MainLayout>
  );
};

export default Subscription;
